import type { Scene } from "@babylonjs/core/scene";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { Observer } from "@babylonjs/core/Misc/observable";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";
import { AccessibilitySystem } from "../../systems/AccessibilitySystem";
import { tagAs } from "../PlaceholderTag";

type Phase = "dormant" | "aligned" | "restored";
const cleanName = (mesh: AbstractMesh): string => mesh.name.replace(/^(Clone of )+/, "");
const JOINTS = ["outer", "middle", "inner"].map(n => "CanopyOrrery_gimbal_" + n);

/** One supported mechanism, one state indicator, and one optional-source lifetime.
 * Puzzle rules remain in TheOrrery; no body, input or game event is owned here. */
export class CanopyOrreryPresentation {
  readonly ready: Promise<boolean>;
  private readonly root: TransformNode;
  private readonly fallback: AbstractMesh[] = [];
  private readonly materials: PBRMaterial[] = [];
  private readonly fallbackCore: PBRMaterial;
  private sourceCore: PBRMaterial | null = null;
  private joints: AbstractMesh[] = [];
  private casters: AbstractMesh[] = [];
  private source: InstantiatedModel | null = null;
  private observer: Observer<Scene> | null;
  private retired = false;
  private phase: Phase = "dormant";

  constructor(private readonly scene: Scene, center: Vector3,
    private readonly shadows: ShadowGenerator) {
    this.root = new TransformNode("canopyOrreryArt", scene); this.root.position.copyFrom(center);
    const brass = this.material("brass", new Color3(.34, .24, .105), .58, .7);
    const iron = this.material("iron", new Color3(.055, .065, .06), .7, .55);
    this.fallbackCore = this.material("core", new Color3(.12, .18, .2), .3, .3);
    const adopt = (m: AbstractMesh, p: Vector3, mat: PBRMaterial): void => {
      m.parent = this.root; m.position.copyFrom(p); m.material = mat;
      m.isPickable = false; m.receiveShadows = true; tagAs(m, "placeholder"); this.fallback.push(m);
    };
    const foot = MeshBuilder.CreateCylinder("canopyOrreryFallback_base",
      { height: .18, diameter: 1.3, tessellation: 24 }, scene);
    adopt(foot, new Vector3(0, -4.84, -.3), iron);
    const stem = MeshBuilder.CreateTube("canopyOrreryFallback_stem", { radius: .10, tessellation: 12,
      path: [new Vector3(0, -4.75, -.85), new Vector3(0, -3.4, -1.3), new Vector3(0, -1.2, -2.2)] }, scene);
    adopt(stem, Vector3.Zero(), iron);
    for (let i = 0; i < 3; i++) {
      const ring = MeshBuilder.CreateTorus("canopyOrreryFallback_ring_" + i,
        { diameter: 4.6 - i * .8, thickness: .065, tessellation: 64 }, scene);
      ring.rotation.set(Math.PI / 2, (i - 1) * .35, i * .2);
      adopt(ring, new Vector3(0, -1.2, -2.2), brass);
    }
    const core = MeshBuilder.CreateSphere("canopyOrreryFallback_core", { diameter: .55, segments: 16 }, scene);
    adopt(core, new Vector3(0, -1.2, -2.2), this.fallbackCore);
    this.registerCasters(this.fallback); this.applyPhase();
    this.observer = scene.onDisposeObservable.addOnce(() => this.dispose());
    this.ready = this.load();
  }

  private material(name: string, color: Color3, roughness: number, metallic: number): PBRMaterial {
    const m = new PBRMaterial("canopyOrrery_" + name, this.scene);
    m.albedoColor = color; m.roughness = roughness; m.metallic = metallic;
    this.materials.push(m); return m;
  }

  private async load(): Promise<boolean> {
    let candidate: InstantiatedModel | null = null;
    try {
      candidate = await getAssetManager().instantiateSharedModel(this.scene,
        "/assets/models/environment/canopy_orrery.glb");
      if (this.retired || this.scene.isDisposed) { candidate.dispose(); return false; }
      if (!candidate.rootNodes.length) throw new Error("Canopy Orrery has no source root");
      const required = ["CanopyOrrery_base", "CanopyOrrery_core", ...JOINTS].map(name => {
        const matches = candidate!.meshes.filter(m => cleanName(m) === name);
        const m = matches[0];
        if (matches.length !== 1 || !m || !m.getTotalVertices() || !m.isEnabled() || !m.isVisible
          || !(m.material instanceof PBRMaterial) || m.material.alpha !== 1
          || m.material.needAlphaBlendingForMesh(m) || m.material.needAlphaTestingForMesh(m)) {
          throw new Error("Incomplete opaque Canopy Orrery: " + name);
        }
        return m;
      });
      for (const root of candidate.rootNodes) root.parent = this.root;
      for (const mesh of candidate.meshes) {
        mesh.isPickable = false; mesh.receiveShadows = true; tagAs(mesh, "modular-final");
      }
      // State belongs to this instance, never the shared cached material.
      this.sourceCore = (required[1].material as PBRMaterial).clone("canopyOrrery_coreState");
      required[1].material = this.sourceCore;
      this.joints = required.slice(2);
      for (const joint of this.joints) if (joint.rotationQuaternion) {
        joint.rotation.copyFrom(joint.rotationQuaternion.toEulerAngles()); joint.rotationQuaternion = null;
      }
      this.clearCasters(); this.registerCasters(candidate.meshes);
      for (const mesh of this.fallback) mesh.isVisible = false;
      this.source = candidate; this.applyPhase(); return true;
    } catch (error) {
      candidate?.dispose();
      if (!this.retired && !this.scene.isDisposed)
        console.warn("[Canopy] Orrery art unavailable; retaining supported fallback:", error);
      return false;
    }
  }

  setPhase(phase: Phase): void { if (!this.retired) { this.phase = phase; this.applyPhase(); } }

  private applyPhase(): void {
    const color = this.phase === "restored" ? new Color3(.18, .28, .25)
      : this.phase === "aligned" ? new Color3(.07, .12, .14) : new Color3(.015, .022, .03);
    this.fallbackCore.emissiveColor.copyFrom(color);
    this.sourceCore?.emissiveColor.copyFrom(color);
  }

  update(dt: number): void {
    if (this.retired || this.phase !== "restored" || AccessibilitySystem.getInstance().state.reducedMotion) return;
    for (const [i, joint] of this.joints.entries()) joint.rotation.z += dt * (i % 2 ? -.055 : .04);
  }

  private registerCasters(meshes: readonly AbstractMesh[]): void {
    this.casters = meshes.filter(m => m.getTotalVertices() > 0);
    for (const m of this.casters) this.shadows.addShadowCaster(m, false);
  }
  private clearCasters(): void {
    for (const m of this.casters) this.shadows.removeShadowCaster(m, false);
    this.casters = [];
  }

  dispose(): void {
    if (this.retired) return;
    this.retired = true;
    if (this.observer) this.scene.onDisposeObservable.remove(this.observer);
    this.observer = null; this.clearCasters(); this.joints = [];
    this.source?.dispose(); this.source = null;
    this.sourceCore?.dispose(); this.sourceCore = null;
    this.root.dispose(); for (const m of this.materials) m.dispose();
  }
}
